import { setLoginBeginCookie, trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { CredentialResponse } from '@react-oauth/google';
import jwtDecode from 'jwt-decode';

interface ThreatMetrixInfo {
    orgId?: string;
    sessionId?: string;
    pageType?: string;
}

export function initGoogleOneTapClient({ clientId, isLoggedIn, isMobile, cooldownMs = 2 * 60 * 60 * 1000 }: { clientId?: string; isLoggedIn: boolean; isMobile: boolean; cooldownMs?: number }) {
    try {
        if (!clientId || isLoggedIn) return;

        // Early exit for bots/crawlers to improve SEO performance
        // Bots don't need device fingerprinting or login functionality
        const isBot = (() => {
            // 1. Check User-Agent (catches ~85-95% of SEO crawlers)
            const userAgent = navigator?.userAgent || '';
            const hasBotUA = /bot|crawler|spider|crawling|googlebot|bingbot|yandex|baiduspider|slurp|duckduckbot|facebookexternalhit|linkedinbot|twitterbot|whatsapp|telegram|lighthouse|google-inspectiontool|mediapartners-google/i.test(userAgent);
            if (hasBotUA) return true;

            // 2. Check for headless browser indicators (increases detection rate)
            if (navigator?.webdriver) return true; // Selenium/WebDriver flag
            
            // 3. Check for missing features that real browsers have
            if (!navigator?.languages || !navigator?.plugins) return true;
            
            // 4. Chrome Headless detection
            if (userAgent.includes('HeadlessChrome')) return true;
            
            return false;
        })();
        
        if (isBot) {
            console.log('Google One Tap: Skipped for bot/crawler');
            return;
        }

        const key = 'oneTapSuppressedUntil';
        const now = Date.now().valueOf();
        const suppressedUntil = Number(localStorage.getItem(key) || 0);
        // console.log('suppressedUntil====', now, suppressedUntil, now < suppressedUntil);
        if (now < suppressedUntil) return;

        // Get ThreatMetrix Data
        const fetchTmxInfo = async (): Promise<ThreatMetrixInfo | null> => {
            try {
                const response = await fetch('/bff-api/tmx/info', {
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                
                if (!response.ok) return null;
                
                const data = await response.json();
                return data.threatMetrixTracking || null;
            } catch (error) {
                console.warn('Failed to fetch TMX info:', error);
                return null;
            }
        };

        // Load ThreatMetrix Script
        const loadTmxScript = (tmxInfo: ThreatMetrixInfo): Promise<void> => {
            return new Promise((resolve) => {
                if (!tmxInfo.orgId || !tmxInfo.sessionId) {
                    resolve();
                    return;
                }

                const pageType = tmxInfo.pageType || '1';

                // Check if the script has already been loaded
                if (document.getElementById('threatmetrix-script')) {
                    resolve();
                    return;
                }

                // Load TMX Main Script
                const script = document.createElement('script');
                script.id = 'threatmetrix-script';
                script.type = 'text/javascript';
                script.innerHTML = `
                    var threatmetrix=threatmetrix||{};threatmetrix.version=3,threatmetrix.create_url=function(t,e,r,n,a){function i(){return Math.floor(2742745743359*Math.random())}function c(){return o(i())}function o(t){return(t+78364164096).toString(36)}var m=i(),u=i(),l=885187064159;u=((u=u-u%256+threatmetrix.version)+m)%2742745743359,l=(l+m)%2742745743359;var s="https://"+t+"/"+(m=c()+o(m))+e,h=[(u=o(l)+o(u))+"="+r,c()+c()+"="+n];return void 0!==a&&a.length>0&&h.push(c()+c()+"="+a),s+"?"+h.join("&")},threatmetrix.beacon=function(t,e,r,n){var a="turn:aa.online-metrix.net?transport=",i="1:"+e+":"+r,c={iceServers:[{urls:a+"tcp",username:i,credential:r},{urls:a+"udp",username:i,credential:r}]};try{var o=new RTCPeerConnection(c);o.createDataChannel(Math.random().toString());var m=function(){},u=function(t){o.setLocalDescription(t,m,m)};"undefined"==typeof Promise||o.createOffer.length>0?o.createOffer(u,m):o.createOffer().then(u,m)}catch(t){}},threatmetrix.load_tags=function(t,e,r,n){threatmetrix.beacon(t,e,r,n);var a=document.getElementsByTagName("head").item(0),i=document.createElement("script");i.id="tmx_tags_js",i.setAttribute("type","text/javascript");var c=threatmetrix.create_url(t,".js",e,r,n);i.setAttribute("src",c),threatmetrix.set_csp_nonce(i),a.appendChild(i)},threatmetrix.load_iframe_tags=function(t,e,r,n){threatmetrix.beacon(t,e,r,n);var a=threatmetrix.create_url(t,".htm",e,r,n),i=document.createElement("iframe");i.title="empty",i.setAttribute("aria-disabled","true"),i.width="0",i.height="0",i.setAttribute("style","color:rgba(0,0,0,0); float:left; position:absolute; top:-200; left:-200; border:0px"),i.setAttribute("src",a),document.body.appendChild(i)},threatmetrix.csp_nonce=null,threatmetrix.register_csp_nonce=function(t){if(void 0!==t.currentScript&&null!==t.currentScript){var e=t.currentScript.getAttribute("nonce");void 0!==e&&null!==e&&""!==e?threatmetrix.csp_nonce=e:void 0!==t.currentScript.nonce&&null!==t.currentScript.nonce&&""!==t.currentScript.nonce&&(threatmetrix.csp_nonce=t.currentScript.nonce)}},threatmetrix.set_csp_nonce=function(t){null!==threatmetrix.csp_nonce&&(t.setAttribute("nonce",threatmetrix.csp_nonce),t.getAttribute("nonce")!==threatmetrix.csp_nonce&&(t.nonce=threatmetrix.csp_nonce))},threatmetrix.cleanup=function(){for(;null!==(hp_frame=document.getElementById("tdz_ifrm"));)hp_frame.parentElement.removeChild(hp_frame);for(;null!==(tmx_frame=document.getElementById("tmx_tags_iframe"));)tmx_frame.parentElement.removeChild(tmx_frame);for(;null!==(tmx_script=document.getElementById("tmx_tags_js"));)tmx_script.parentElement.removeChild(tmx_script)},threatmetrix.profile=function(t,e,r,n){threatmetrix.cleanup(),threatmetrix.register_csp_nonce(document),threatmetrix.load_tags(t,e,r,n)},threatmetrix.profile_iframe=function(t,e,r,n){threatmetrix.cleanup(),threatmetrix.register_csp_nonce(document),threatmetrix.load_iframe_tags(t,e,r,n)};
                `;
                document.head.appendChild(script);

                // Execute TMX profile
                const profileScript = document.createElement('script');
                profileScript.id = 'threatmetrix-profile-script';
                profileScript.type = 'text/javascript';
                profileScript.innerHTML = `
                    threatmetrix.profile("cstm.gumtree.com", "${tmxInfo.orgId}", "${tmxInfo.sessionId}", "${pageType}");
                `;
                document.head.appendChild(profileScript);

                // TMX script loading is asynchronous, give it some time to complete fingerprint collection
                setTimeout(() => resolve(), 500);
            });
        };

        const ensureGsiLoaded = (timeoutMs = 10000): Promise<void> => {
            return new Promise((resolve, reject) => {
                if ((window as any).google?.accounts?.id) return resolve();
                
                const timeout = setTimeout(() => {
                    reject(new Error('Google GSI script load timeout'));
                }, timeoutMs);
                
                const s = document.createElement('script');
                s.async = true;
                s.defer = true;
                s.src = 'https://accounts.google.com/gsi/client';
                s.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                s.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Failed to load Google GSI script'));
                };
                (document.head || document.body).appendChild(s);
            });
        };

        const onCredentialResponse = ({ credential }: CredentialResponse) => {
            try {
                if (!credential) return;
                const { email } = jwtDecode<{ email: string }>(credential);
                fetch('/bff-api/login/via-social', {
                    method: 'POST',
                    headers: { 'content-type': 'application/x-www-form-urlencoded' },
                    credentials: 'include',
                    body: new URLSearchParams({ username: email, password: credential, loginPlatform: 'googleplus' }) as any,
                })
                    .then((resp: any) => {
                        // console.log('login resp====', resp);
                        if(resp?.ok) {
                            setLoginBeginCookie('googleOneTap');
                            window.location.reload()
                        }
                    })
                    .catch(() => {});
            } catch (_e) {
                console.log('error====', _e);
            }
        };

        const start = () => {
            const g = (window as any).google?.accounts?.id;
            if (!g) {
                // Limit retries to prevent infinite loops in test environments
                const retryCount = (start as any).retryCount || 0;
                if (retryCount >= 5) { // Max 5 retries = ~1 second
                    console.warn('Google One Tap: GSI not available after retries');
                    return;
                }
                (start as any).retryCount = retryCount + 1;
                setTimeout(start, 200);
                return;
            }
            (start as any).retryCount = 0; // Reset on success

            trackGA4Event({ event: 'google_one_tap_init', userAccountType: 'googleOneTap' } as any);
            g.initialize({ 
                client_id: clientId, 
                callback: onCredentialResponse, 
                use_fedcm_for_prompt: isMobile ? false : true,
                cancel_on_tap_outside: false,
            });
            g.prompt((n: any) => {
                console.log('prompt====', n);
                try {
                    if (n && n.isDisplayMoment && n.isDisplayMoment()) {
                        // console.log('display moment====', n);
                    }
                    if (n && n.isNotDisplayed && n.isNotDisplayed()) {
                        // const reasonNd = n.getNotDisplayedReason && n.getNotDisplayedReason();
                        // console.log('not displayed====', n, reasonNd);
                    }
                    if (n && n.isSkippedMoment && n.isSkippedMoment()) {
                        // const reasonSk = n.getSkippedReason && n.getSkippedReason();
                        // console.log('skipped moment====', n, reasonSk)
                        localStorage.setItem(key, String(Date.now() + cooldownMs));
                    }
                    if (n && n.isDismissedMoment && n.isDismissedMoment()) {
                        // console.log('dismissed====', n);
                    }
                } catch (_e) {
                    console.log('prompt error====', _e);
                }
            });
        };

        const run = async () => {
            try {
                // Step 1: Get TMX data
                const tmxInfo = await fetchTmxInfo();
                
                // Step 2: Load TMX script for device fingerprint collection
                if (tmxInfo?.sessionId) {
                    await loadTmxScript(tmxInfo);
                    console.log('ThreatMetrix loaded for Google One Tap');
                }
                
                // Step 3: Load One Tap script
                await ensureGsiLoaded();
                start();
            } catch (err) {
                // Silently fail in test environments or when script fails to load
                console.warn('Google One Tap initialization failed:', err);
            }
        };
        
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run, { once: true });
    } catch (_e) {
        console.log('init error====', _e);
    }
}

export default initGoogleOneTapClient;


